import React, { useEffect, useRef } from 'react';
import { Box, Button, Card, CardContent, CircularProgress, Typography, alpha } from '@mui/material';
import { SaveOutlined as DraftIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { ACCENT, TEXT_PRIMARY, TEXT_SECONDARY, SUCCESS, glassCardSx } from './SaaSTokens';
import { formatDraftTimestamp } from '../../utils/formDraftStorage';

const MotionCard = motion(Card);

const findFirstVisibleError = (form) => {
  if (!form) return null;
  const candidates = form.querySelectorAll('[aria-invalid="true"], .MuiFormControl-root.Mui-error, .Mui-error');
  return Array.from(candidates).find((element) => {
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }) || null;
};

const scrollToError = (element) => {
  if (!element) return false;
  const focusTarget = element.matches('input, textarea, select, button, [tabindex]')
    ? element
    : element.querySelector('input, textarea, select, button, [tabindex]');
  element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  window.setTimeout(() => focusTarget?.focus({ preventScroll: true }), 350);
  return true;
};

/**
 * Shared submit action card for forms.
 * Keeps styling consistent with the SaaS dark theme and removes neon/glow variants.
 */
const SubmitActionsCard = ({
  statusText,
  missingRequiredFields = null,
  readyStatusText = 'All required fields are complete. Ready to submit.',
  cancelLabel,
  onCancel,
  cancelDisabled = false,
  submitLabel,
  submitDisabled = false,
  loading = false,
  submitStartIcon = null,
  showSubmit = true,
  submitType = 'submit',
  showSaveDraft = false,
  onSaveDraft,
  saveDraftLabel = 'Save Draft',
  draftSavedAt = null,
}) => {
  const observerRef = useRef(null);
  const observerTimeoutRef = useRef(null);
  const draftHint = draftSavedAt ? `Draft saved ${formatDraftTimestamp(draftSavedAt)}` : null;
  const requiredStatus = Array.isArray(missingRequiredFields)
    ? (missingRequiredFields.length > 0
      ? `Required fields remaining: ${missingRequiredFields.join(', ')}.`
      : readyStatusText)
    : null;
  const displayStatus = requiredStatus || draftHint || statusText;

  const watchForValidationError = (event) => {
    const form = event.currentTarget.closest('form');
    if (!form) return;
    observerRef.current?.disconnect();
    window.clearTimeout(observerTimeoutRef.current);

    const revealFirstError = () => {
      const firstError = findFirstVisibleError(form);
      if (!scrollToError(firstError)) return false;
      observerRef.current?.disconnect();
      window.clearTimeout(observerTimeoutRef.current);
      return true;
    };

    window.setTimeout(revealFirstError, 0);
    const observer = new MutationObserver(revealFirstError);
    observer.observe(form, {
      subtree: true,
      childList: true,
      attributes: true,
      // Watching every class mutation is expensive while buttons, spinners, and
      // animated fields are updating. Validation inputs expose aria-invalid,
      // while helper/error elements are covered by childList changes.
      attributeFilter: ['aria-invalid'],
    });
    observerRef.current = observer;
    observerTimeoutRef.current = window.setTimeout(() => observer.disconnect(), 10000);
  };

  useEffect(() => () => {
    observerRef.current?.disconnect();
    window.clearTimeout(observerTimeoutRef.current);
  }, []);

  return (
    <MotionCard
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
      elevation={0}
      sx={glassCardSx}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 }, position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) auto' },
            justifyContent: 'stretch',
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
            <Box
              sx={{
                position: 'relative',
                width: 8,
                height: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 0.55,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: alpha(SUCCESS, 0.45),
                  boxShadow: 'none',
                  animation: 'none',
                  flexShrink: 0,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: -3,
                  borderRadius: '50%',
                  border: `1px solid ${alpha(SUCCESS, 0.25)}`,
                  animation: 'none',
                  opacity: 0.95,
                }}
              />
            </Box>

            <Typography sx={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: { xs: '0.82rem', sm: '0.875rem' },
              lineHeight: 1.55,
              color: alpha(TEXT_SECONDARY, 0.95),
              minWidth: 0,
              overflowWrap: 'anywhere',
            }}>
              {displayStatus}
            </Typography>
          </Box>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: showSubmit ? 'repeat(2, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, max-content)',
            },
            gap: 1.5,
            minWidth: 0,
            width: { xs: '100%', lg: 'auto' },
            justifyContent: { xs: 'stretch', lg: 'end' },
            '& .MuiButton-root': {
              width: '100%',
              minWidth: { xs: 0, sm: 150, lg: 150 },
              whiteSpace: 'normal',
              textAlign: 'center',
              lineHeight: 1.2,
              minHeight: 44,
              '& .MuiButton-startIcon': {
                flexShrink: 0,
              },
            },
          }}>
          {cancelLabel ? (
              <Button
                variant="outlined"
                onClick={onCancel}
                disabled={cancelDisabled}
                sx={{
                  order: { xs: 3, sm: 2, lg: 1 },
                  borderRadius: '10px',
                  px: 3,
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderColor: 'rgba(255,255,255,0.14)',
                  color: alpha(TEXT_PRIMARY, 0.9),
                  background: alpha(TEXT_PRIMARY, 0.02),
                  transition: 'all 0.18s ease',
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: alpha(ACCENT, 0.45),
                    background: alpha(ACCENT, 0.08),
                    boxShadow: 'none',
                    color: TEXT_PRIMARY,
                  },
                }}
              >
                {cancelLabel}
              </Button>
            ) : null}
            
            {showSaveDraft && onSaveDraft ? (
              <Button
                type="button"
                variant="outlined"
                startIcon={<DraftIcon />}
                onClick={onSaveDraft}
                disabled={cancelDisabled || loading}
                sx={{
                  order: { xs: 2, sm: 3, lg: 2 },
                  borderRadius: '10px',
                  px: 2.5,
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderColor: alpha(SUCCESS, 0.35),
                  color: alpha(TEXT_PRIMARY, 0.9),
                  background: alpha(SUCCESS, 0.04),
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: alpha(SUCCESS, 0.55),
                    background: alpha(SUCCESS, 0.1),
                    boxShadow: 'none',
                  },
                }}
              >
                {saveDraftLabel}
              </Button>
            ) : null}

            {showSubmit ? (
              <Button
                type={submitType}
                onClick={watchForValidationError}
                variant="contained"
                startIcon={loading ? null : submitStartIcon}
                disabled={loading || submitDisabled}
                sx={{
                  order: { xs: 1, lg: 3 },
                  gridColumn: { xs: '1', sm: showSaveDraft && cancelLabel ? '1 / -1' : 'auto', lg: 'auto' },
                  borderRadius: '10px',
                  px: 4,
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  letterSpacing: 0.3,
                  background: alpha(ACCENT, 0.18),
                  border: `1px solid ${alpha(ACCENT, 0.35)}`,
                  color: TEXT_PRIMARY,
                  boxShadow: 'none',
                  transition: 'all 0.18s ease',
                  '&:hover': {
                    background: alpha(ACCENT, 0.26),
                    border: `1px solid ${alpha(ACCENT, 0.55)}`,
                    boxShadow: 'none',
                    transform: 'none',
                    color: '#fff',
                  },
                  '&:active': { transform: 'none' },
                  '&.Mui-disabled': {
                    background: alpha(SUCCESS, 0.06),
                    color: alpha(TEXT_SECONDARY, 0.35),
                    boxShadow: 'none',
                    border: '1px solid rgba(255,255,255,0.10)',
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={19} sx={{ color: alpha(ACCENT, 0.85) }} />
                ) : (
                  submitLabel
                )}
              </Button>
            ) : null}
          </Box>
        </Box>
      </CardContent>
    </MotionCard>
  );
};

export default SubmitActionsCard;
